/**
 * DOM construction and styling for the touch-control overlay.
 *
 * Pure view layer: all input tracking logic lives in `touch.ts`. The overlay
 * root is `pointer-events: none` so touches fall through to the canvas (which
 * owns joystick/aim tracking); only the on-screen buttons opt back into
 * pointer events.
 */

import { makeHapticSwitch } from './haptics.js';

export interface TouchControlsDOM {
  root: HTMLDivElement;
  joystickBase: HTMLDivElement;
  joystickNub: HTMLDivElement;
  pauseButton: HTMLButtonElement;
  pauseSwitch: HTMLInputElement;
}

export const JOYSTICK_BASE_PX = 110;
export const JOYSTICK_NUB_PX = 48;
/** On-screen buttons; comfortably above the 44px minimum touch target. */
export const TOUCH_BUTTON_PX = 56;

function circle(el: HTMLElement, sizePx: number): void {
  el.style.width = `${sizePx}px`;
  el.style.height = `${sizePx}px`;
  el.style.borderRadius = '50%';
}

function styleButton(btn: HTMLButtonElement, sizePx: number): void {
  btn.style.position = 'absolute';
  btn.style.width = `${sizePx}px`;
  btn.style.height = `${sizePx}px`;
  btn.style.pointerEvents = 'auto';
  btn.style.touchAction = 'none';
  btn.style.userSelect = 'none';
  (btn.style as CSSStyleDeclaration & { webkitUserSelect?: string }).webkitUserSelect =
    'none';
  (
    btn.style as CSSStyleDeclaration & { webkitTapHighlightColor?: string }
  ).webkitTapHighlightColor = 'transparent';
  btn.style.border = '2px solid #1e5b7a';
  btn.style.background = 'rgba(7, 21, 38, 0.55)';
  btn.style.color = '#e8f4ff';
  btn.style.fontFamily = 'inherit';
  btn.style.fontSize = '11px';
  btn.style.lineHeight = '1';
  btn.style.display = 'flex';
  btn.style.alignItems = 'center';
  btn.style.justifyContent = 'center';
  btn.style.cursor = 'pointer';
  btn.style.padding = '0';
  btn.style.margin = '0';
}

/**
 * Build the touch overlay DOM tree. The caller appends `root` to
 * `document.body` and owns all event wiring.
 */
export function createTouchControlsDOM(): TouchControlsDOM {
  const root = document.createElement('div');
  root.id = 'touch-controls';
  root.style.position = 'fixed';
  root.style.inset = '0';
  root.style.pointerEvents = 'none';
  root.style.zIndex = '40';
  root.style.touchAction = 'none';

  // Virtual joystick: floating base + nub, hidden until the left half of the
  // screen is touched.
  const joystickBase = document.createElement('div');
  circle(joystickBase, JOYSTICK_BASE_PX);
  joystickBase.style.position = 'absolute';
  joystickBase.style.display = 'none';
  joystickBase.style.transform = 'translate(-50%, -50%)';
  joystickBase.style.border = '2px solid rgba(143, 179, 204, 0.5)';
  joystickBase.style.background = 'rgba(7, 21, 38, 0.35)';
  joystickBase.style.boxShadow = '0 0 14px rgba(30, 91, 122, 0.6)';
  joystickBase.style.boxSizing = 'border-box';

  const joystickNub = document.createElement('div');
  circle(joystickNub, JOYSTICK_NUB_PX);
  joystickNub.style.position = 'absolute';
  joystickNub.style.left = '50%';
  joystickNub.style.top = '50%';
  joystickNub.style.transform = 'translate(-50%, -50%)';
  joystickNub.style.background = 'rgba(255, 211, 107, 0.85)';
  joystickNub.style.boxShadow = '0 0 10px rgba(255, 211, 107, 0.7)';

  joystickBase.appendChild(joystickNub);

  // Pause: top-right corner. (Dash is a double-tap anywhere — no dedicated
  // button; hold steers, quick tap fires.) The button is wrapped with an
  // iOS haptic switch overlay (makeHapticSwitch): a real tap on it ticks
  // natively and still reaches the pause handlers (attached to both).
  const pauseButton = document.createElement('button');
  pauseButton.type = 'button';
  pauseButton.textContent = 'II';
  styleButton(pauseButton, 48);
  pauseButton.style.position = 'relative';
  pauseButton.style.width = '100%';
  pauseButton.style.height = '100%';
  pauseButton.style.borderRadius = '10px';

  const pauseWrap = document.createElement('div');
  pauseWrap.className = 'la-haptic-wrap';
  pauseWrap.style.position = 'absolute';
  pauseWrap.style.width = '48px';
  pauseWrap.style.height = '48px';
  pauseWrap.style.pointerEvents = 'auto';
  pauseWrap.style.right = 'calc(12px + env(safe-area-inset-right, 0px))';
  pauseWrap.style.top = 'calc(12px + env(safe-area-inset-top, 0px))';
  pauseWrap.appendChild(pauseButton);
  const pauseSwitch = makeHapticSwitch();
  pauseWrap.appendChild(pauseSwitch);

  root.appendChild(joystickBase);
  root.appendChild(pauseWrap);

  return { root, joystickBase, joystickNub, pauseButton, pauseSwitch };
}

/** Show the joystick base at a client-space origin with the nub centered. */
export function showJoystick(dom: TouchControlsDOM, clientX: number, clientY: number): void {
  dom.joystickBase.style.display = 'block';
  dom.joystickBase.style.left = `${clientX}px`;
  dom.joystickBase.style.top = `${clientY}px`;
  moveJoystickNub(dom, 0, 0);
}

/** Offset the nub from the base center (client-space pixels). */
export function moveJoystickNub(dom: TouchControlsDOM, dx: number, dy: number): void {
  dom.joystickNub.style.transform = `translate(-50%, -50%) translate(${dx}px, ${dy}px)`;
}

export function hideJoystick(dom: TouchControlsDOM): void {
  dom.joystickBase.style.display = 'none';
}

/** Lightweight pressed-state feedback for the on-screen buttons. */
export function setButtonPressed(btn: HTMLButtonElement, pressed: boolean): void {
  btn.style.opacity = pressed ? '1' : '0.75';
  btn.style.transform = pressed ? 'scale(0.92)' : 'scale(1)';
}
